import { CommonModule } from '@angular/common';
import { Component, Input, ViewChild, ElementRef, AfterViewInit, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { TagModule, Tag } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { EMPLOYEE_STATUS_CONFIG } from '@shared/enums/employee-status.enum';
import { EMPLOYEE_POSITION_STATUS_CONFIG } from '@shared/enums/employee-position-status.enum';
import { HOST_ACC_STATUS_CONFIG } from '@shared/enums/host-acc-status.enum';
import { HOST_ACC_RENEWAL_TYPE_CONFIG } from '@shared/enums/host-acc-renewal-type.enum';
import { PROBATION_STATUS_CONFIG } from '@shared/enums/probation-status.enum';
import { ROOM_STATUS_CONFIG } from '@shared/enums/room-status.enum';
import { TRAINING_TYPE_CONFIG, TRAINING_STATUS_CONFIG, TA_TASK_TYPE_CONFIG } from '@shared/enums/training.enum';
import { ADDRESS_TYPE_CONFIG } from '@shared/enums/address-type.enum';
import { COURSE_TYPE_CONFIG, COURSE_STATUS_CONFIG } from '@shared/enums/course.enum';
import { ENROLMENT_STATUS_CONFIG, ENROLMENT_TASK_TYPE_CONFIG, ENROLMENT_TASK_STATUS_CONFIG } from '@shared/enums/enrolment.enum';

export type TagType = 'employeeStatus' | 'employeePositionStatus' | 'hostAccStatus' | 'hostAccRenewalType' | 'probationStatus' | 'roomStatus' | 'trainingType' | 'trainingStatus' | 'taTaskType' | 'addressType' | 'courseType' | 'courseStatus' | 'enrolmentStatus' | 'enrolmentTaskType' | 'enrolmentTaskStatus';

interface TagConfig {
    label: string;
    cssClass: string;
}

@Component({
    selector: 'generic-p-tag',
    standalone: true,
    imports: [
        CommonModule,
        TagModule,
        TooltipModule
    ],
    template: `
        <p-tag #tagElement
               [value]="tagLabel"
               [styleClass]="convertedStyleClass"
               [style]="customStyle"
               [pTooltip]="shouldShowTooltip ? tagLabel : ''"
               tooltipPosition="top">
        </p-tag>
    `
})
export class GenericTagComponent implements OnChanges, AfterViewInit {
    @Input() value!: string;
    @Input() type?: TagType;
    @Input() styleClass?: string; // Additional CSS classes
    @Input() tagColor?: string; // Custom background color
    @Input() tagTextColor?: string; // Custom text color

    @ViewChild('tagElement', { static: false }) tagElement!: Tag;

    shouldShowTooltip = false;
    tagLabel: string = '';
    convertedStyleClass: string = '';
    customStyle: any = {};

    private readonly statusConfigs = {
        employeeStatus: EMPLOYEE_STATUS_CONFIG,
        employeePositionStatus: EMPLOYEE_POSITION_STATUS_CONFIG,
        hostAccStatus: HOST_ACC_STATUS_CONFIG,
        hostAccRenewalType: HOST_ACC_RENEWAL_TYPE_CONFIG,
        probationStatus: PROBATION_STATUS_CONFIG,
        roomStatus: ROOM_STATUS_CONFIG,
        trainingType: TRAINING_TYPE_CONFIG,
        trainingStatus: TRAINING_STATUS_CONFIG,
        taTaskType: TA_TASK_TYPE_CONFIG,
        addressType: ADDRESS_TYPE_CONFIG,
        courseType: COURSE_TYPE_CONFIG,
        courseStatus: COURSE_STATUS_CONFIG,
        enrolmentStatus: ENROLMENT_STATUS_CONFIG,
        enrolmentTaskType: ENROLMENT_TASK_TYPE_CONFIG,
        enrolmentTaskStatus: ENROLMENT_TASK_STATUS_CONFIG
    };

    ngOnChanges(changes: SimpleChanges): void {
        // Update tag display when inputs change
        if (changes['value'] || changes['type'] || changes['styleClass'] || changes['tagColor'] || changes['tagTextColor']) {
            this.updateTag();
        }
    }

    ngAfterViewInit(): void {
        this.updateTag();
    }

    private updateTag(): void {
        // Update display properties
        const config = this.getConfig();
        const label = config?.label || this.value;

        // Handle styling based on priority: custom colors > enum config > default
        if (this.tagColor || this.tagTextColor) {
            // Custom colors provided - use minimal CSS classes and apply colors via style
            this.convertedStyleClass = this.styleClass ? `text-sm ${this.styleClass}` : 'text-sm';
            this.customStyle = {
                ...(this.tagColor && { 'background-color': this.tagColor }),
                ...(this.tagTextColor && { color: this.tagTextColor })
            };
        } else {
            // Use enum config or default styling
            const baseCssClass = config?.cssClass || 'text-sm !bg-gray-100 !text-gray-700';
            this.convertedStyleClass = this.styleClass ? `${baseCssClass} ${this.styleClass}` : baseCssClass;
            this.customStyle = {};
        }

        this.tagLabel = label;

        // Check overflow after DOM updates, but only if view is initialized
        if (this.tagElement) {
            setTimeout(() => {
                this.checkOverflow();
            });
        }
    }

    private checkOverflow(): void {
        if (this.tagElement?.el?.nativeElement) {
            const element = this.tagElement.el.nativeElement;
            const labelElement = element.querySelector('.p-tag-label') as HTMLElement;
            const style = getComputedStyle(element);

            if (labelElement) {
                // Store original styles
                const originalOverflow = labelElement.style.overflow;
                const originalTextOverflow = labelElement.style.textOverflow;
                const originalWhiteSpace = labelElement.style.whiteSpace;

                // Temporarily remove overflow restrictions to get true content width
                labelElement.style.overflow = 'visible';
                labelElement.style.textOverflow = 'unset';
                labelElement.style.whiteSpace = 'nowrap';

                // Get the actual content width and available width
                const contentWidth = labelElement.scrollWidth;
                const paddingLeft = parseFloat(style.paddingLeft);
                const paddingRight = parseFloat(style.paddingRight);
                const availableWidth = element.clientWidth - paddingLeft - paddingRight;

                // Restore original styles
                labelElement.style.overflow = originalOverflow;
                labelElement.style.textOverflow = originalTextOverflow;
                labelElement.style.whiteSpace = originalWhiteSpace;

                // Check if content overflows
                this.shouldShowTooltip = contentWidth > availableWidth;
            }
        } else {
            console.log('Tag element not found');
        }
    }

    private getConfig(): TagConfig | undefined {
        if (!this.type) {
            return undefined;
        }
        const statusConfig = this.statusConfigs[this.type];
        if (!statusConfig) {
            return undefined;
        }
        return (statusConfig as any)[this.value];
    }
}
